import { apiClient } from 'services/api-client';
import type { Action } from 'kbar';
import type { NavigateFunction } from 'react-router';
import { useNavigate } from 'react-router';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { useHasPermission } from 'hooks/use-has-permission';
import { useGetUserPermissions } from 'stores/rq/user-permissions';
import type { Environment, ProjectTreeNode } from 'types/api.types';
import { useAsyncPromise } from 'hooks/use-async-promise';
import { useCuratedProjectsTree } from 'stores/rq/projects';
import { validCharactersRegexp } from 'schemas/deploy-schema';
import { useDebounce } from 'ahooks';
import uniqBy from 'lodash/uniqBy';
import useStores from 'hooks/use-stores.hooks';
import type { EnvironmentsStore } from 'stores/mobx/environments.store';
import { getProjectNameHierarchy } from 'utils/sub-project.utils';

type UseEnvironmentSearchActionsProps = {
  searchTerm: string;
  enabled: boolean;
};

const ENVIRONMENT_SEARCH_RESULTS = 20;
const SEARCH_DEBOUNCE_MS = 50;
const fetchFilteredEnvironments = async (searchTerm: string, currentOrganizationId: string) => {
  if (!currentOrganizationId || !validCharactersRegexp.test(searchTerm)) {
    return [];
  }

  const { data: environments } = await apiClient.environments.getAll({
    organizationId: currentOrganizationId,
    limit: ENVIRONMENT_SEARCH_RESULTS,
    searchText: searchTerm,
    onlyMy: false,
    isActive: true
  });
  return environments;
};

const mapEnvironmentsToActions = (
  environments: Environment[],
  projects: Record<string, ProjectTreeNode>,
  navigate: NavigateFunction,
  environmentsStore: EnvironmentsStore
) =>
  environments.flatMap<Action>(environment => {
    const project = projects[environment.projectId];
    if (project?.isArchived) return [];

    const performNavigation = () => {
      environmentsStore.updateCache(environment.projectId);
      navigate(`/p/${environment.projectId}/environments/${environment.id}`);
    };

    // APO-614: the tree and the environment search are independent async sources, so a missing
    // project (failed/empty/stale tree) must not drop the environment. Without the project we can't
    // build the PROJECT_*_SECTION parent (kbar throws on an unregistered parent), so the action goes
    // in flat. The archived filter above stays — that one is intentional (ENG-837).
    if (!project) {
      return [
        {
          id: environment.id,
          keywords: `${environment.id} ${environment.name} ${environment.workspaceName} ${environment.projectId}`,
          name: environment.name,
          section: 'Environments',
          perform: performNavigation
        }
      ];
    }

    const projectNameHierarchy = getProjectNameHierarchy(environment.projectId, projects) ?? project.name;
    const projectKeywords = `${project.id} ${project.name} ${projectNameHierarchy}`;

    return [
      {
        id: `PROJECT_${environment.projectId}_SECTION`,
        name: projectNameHierarchy,
        section: 'projects',
        keywords: projectKeywords,
        priority: 2
      },
      {
        id: environment.id,
        keywords: `${environment.id} ${environment.name} ${environment.workspaceName} ${projectKeywords}`,
        name: environment.name,
        section: 'Environments',
        parent: `PROJECT_${environment.projectId}_SECTION`,
        perform: performNavigation
      }
    ];
  });

const useHasEnvironmentViewAssignment = () => {
  const { isPlaceholderData: isLoading, data: userPermissionsByScope } = useGetUserPermissions();

  // we don't use `hasPermission` here because we want to check if the user has any environment assignment, but we don't have A specific environment id
  const isAuthorized =
    userPermissionsByScope?.environments ??
    Object.values(userPermissionsByScope?.environments ?? {}).some(env => env.permissions.includes('VIEW_ENVIRONMENT'));

  return { isAuthorized, isLoading };
};

export const useEnvironmentSearchActions = ({ searchTerm, enabled }: UseEnvironmentSearchActionsProps) => {
  const navigate = useNavigate();
  const currentOrganizationId = useCurrentOrganizationId();
  const { projects, isLoading: isLoadingProjects } = useCuratedProjectsTree();
  const { environmentsStore } = useStores();

  const debouncedSearchTerm = useDebounce(searchTerm, { wait: SEARCH_DEBOUNCE_MS });

  const { isAuthorized, isLoading } = useHasPermission(['VIEW_ENVIRONMENT', 'VIEW_PROJECT'], {
    permissionOperator: 'OR'
  });
  const { isAuthorized: isAssignedToAnyEnvironment, isLoading: isLoadingEnvAssignments } =
    useHasEnvironmentViewAssignment();

  const isSearchTermDebouncing = searchTerm !== debouncedSearchTerm;

  const [environmentsError, filteredEnvironmentActions, isLoadingEnvironments] = useAsyncPromise(async () => {
    if (
      !enabled ||
      !(isAuthorized || isAssignedToAnyEnvironment) ||
      isLoading ||
      isLoadingProjects ||
      isLoadingEnvAssignments
    ) {
      return [];
    }

    return fetchFilteredEnvironments(debouncedSearchTerm, currentOrganizationId).then(environments =>
      mapEnvironmentsToActions(environments, projects, navigate, environmentsStore)
    );
  }, [
    enabled,
    isAuthorized,
    isAssignedToAnyEnvironment,
    isLoading,
    isLoadingProjects,
    isLoadingEnvAssignments,
    debouncedSearchTerm,
    currentOrganizationId,
    projects,
    navigate,
    environmentsStore
  ]);

  const isLoadingSearch =
    enabled &&
    searchTerm.length > 0 &&
    (isSearchTermDebouncing || isLoadingEnvironments || isLoading || isLoadingProjects || isLoadingEnvAssignments);
  const actions = uniqBy(filteredEnvironmentActions ?? [], 'id');

  return { actions, isLoadingSearch, environmentsError };
};
